import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Autocomplete,
  Chip,
  IconButton,
  Alert,
  Grid,
  Divider,
  FormControlLabel,
  Switch,
  FormHelperText,
  CircularProgress,
  Paper,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Tooltip,
  Badge,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  FamilyRestroom as FamilyIcon,
  Favorite as FavoriteIcon,
  People as PeopleIcon,
  AccountTree as AccountTreeIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Place as PlaceIcon,
  Event as EventIcon,
  Source as SourceIcon,
  NavigateNext as NavigateNextIcon,
  NavigateBefore as NavigateBeforeIcon,
} from '@mui/icons-material';
import { 
  RelationshipFormData, 
  RelationshipType,
  PersonSearchResult,
  RelationshipValidation,
  LocationFormData
} from '../../types';
import { PersonService } from '../../services/personService';
import { RelationshipService } from '../../services/relationshipService';
import { tokens, lightColors as colors } from '../../theme';

interface RelationshipBuilderProps {
  personId: string;
  onRelationshipCreated: (relationshipData: RelationshipFormData) => Promise<void>;
  onCancel: () => void;
  open?: boolean;
}

interface RelationshipFormState extends Omit<RelationshipFormData, 'marriagePlace' | 'divorcePlace'> {
  marriagePlace: {
    city: string;
    state: string;
    country: string;
  };
  divorcePlace: {
    city: string;
    state: string;
    country: string;
  };
}

const relationshipTypeOptions: Array<{ 
  value: RelationshipType; 
  label: string; 
  icon: React.ReactNode;
  description: string;
  category: 'immediate' | 'extended' | 'other';
}> = [
  { 
    value: 'spouse', 
    label: 'Spouse', 
    icon: <FavoriteIcon />, 
    description: 'Married partner',
    category: 'immediate'
  },
  { 
    value: 'parent', 
    label: 'Parent', 
    icon: <PersonIcon />, 
    description: 'Biological or legal parent',
    category: 'immediate'
  },
  { 
    value: 'child', 
    label: 'Child', 
    icon: <PersonIcon />, 
    description: 'Biological or legal child',
    category: 'immediate'
  },
  { 
    value: 'sibling', 
    label: 'Sibling', 
    icon: <PeopleIcon />, 
    description: 'Brother or sister',
    category: 'immediate'
  },
  { 
    value: 'adoptive-parent', 
    label: 'Adoptive Parent', 
    icon: <PersonIcon />, 
    description: 'Parent through adoption',
    category: 'extended'
  },
  { 
    value: 'adoptive-child', 
    label: 'Adoptive Child', 
    icon: <PersonIcon />, 
    description: 'Child through adoption',
    category: 'extended'
  },
  { 
    value: 'step-parent', 
    label: 'Step Parent', 
    icon: <PersonIcon />, 
    description: 'Parent through marriage',
    category: 'extended'
  },
  { 
    value: 'step-child', 
    label: 'Step Child', 
    icon: <PersonIcon />, 
    description: 'Child through marriage',
    category: 'extended'
  },
  { 
    value: 'guardian', 
    label: 'Guardian', 
    icon: <PersonIcon />, 
    description: 'Legal guardian',
    category: 'other'
  },
  { 
    value: 'godparent', 
    label: 'Godparent', 
    icon: <PersonIcon />, 
    description: 'Spiritual parent',
    category: 'other'
  },
  { 
    value: 'other', 
    label: 'Other', 
    icon: <AccountTreeIcon />, 
    description: 'Other family relationship',
    category: 'other'
  },
];

const stepTitles = [
  'Select Person',
  'Choose Relationship Type', 
  'Add Details',
  'Review & Create'
];

export const RelationshipBuilder: React.FC<RelationshipBuilderProps> = ({
  personId,
  onRelationshipCreated,
  onCancel,
  open = false,
}) => {
  // State management
  const [activeStep, setActiveStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<PersonSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState<PersonSearchResult | null>(null);
  
  // Form state
  const [formData, setFormData] = useState<RelationshipFormState>({
    type: 'spouse',
    personId1: personId,
    personId2: '',
    marriageDate: '',
    marriagePlace: { city: '', state: '', country: '' },
    divorceDate: '',
    divorcePlace: { city: '', state: '', country: '' },
    marriageEndReason: undefined,
    adoptionDate: '',
    isAdopted: false,
    isBiological: true,
    isStepRelationship: false,
    notes: '',
    sources: '',
  });

  // Validation state
  const [validation, setValidation] = useState<RelationshipValidation | null>(null);
  const [isValidating, setIsValidating] = useState(false);

  // Search for persons with debouncing
  const searchPersons = useCallback(async (term: string) => {
    if (!term || term.length < 2) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const results = await PersonService.searchPersons(term);
      // Filter out the current person
      const filteredResults = results.filter(person => person.id !== personId);
      setSearchResults(filteredResults);
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, [personId]);

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchPersons(searchTerm);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, searchPersons]);

  // Real-time validation
  useEffect(() => {
    if (formData.personId1 && formData.personId2 && formData.type) {
      validateFormData();
    }
  }, [formData.personId1, formData.personId2, formData.type, formData.marriageDate, formData.divorceDate]);

  const validateFormData = useCallback(async () => {
    if (!formData.personId1 || !formData.personId2 || !formData.type) return;
    
    setIsValidating(true);
    try {
      const relationshipFormData: RelationshipFormData = {
        ...formData,
        marriagePlace: formData.marriagePlace as LocationFormData,
        divorcePlace: formData.divorcePlace as LocationFormData,
      };
      
      const result = await RelationshipService.validateRelationship(relationshipFormData);
      setValidation(result);
    } catch (error) {
      console.error('Validation error:', error);
    } finally {
      setIsValidating(false);
    }
  }, [formData]);

  const handlePersonSelect = (person: PersonSearchResult | null) => {
    setSelectedPerson(person);
    if (person) {
      setFormData(prev => ({ ...prev, personId2: person.id }));
    } else {
      setFormData(prev => ({ ...prev, personId2: '' }));
    }
  };

  const handleRelationshipTypeSelect = (type: RelationshipType) => {
    setFormData(prev => ({ ...prev, type }));
  };

  const handleNext = () => {
    setActiveStep(prev => prev + 1);
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
    setSelectedPerson(null);
    setSearchTerm('');
    setSearchResults([]);
    setFormData({
      type: 'spouse',
      personId1: personId,
      personId2: '',
      marriageDate: '',
      marriagePlace: { city: '', state: '', country: '' },
      divorceDate: '',
      divorcePlace: { city: '', state: '', country: '' },
      marriageEndReason: undefined,
      adoptionDate: '',
      isAdopted: false,
      isBiological: true,
      isStepRelationship: false,
      notes: '',
      sources: '',
    });
    setValidation(null);
  };

  const handleCreate = async () => {
    if (!validation?.isValid) return;
    
    setIsLoading(true);
    try {
      const relationshipFormData: RelationshipFormData = {
        ...formData,
        marriagePlace: formData.marriagePlace as LocationFormData,
        divorcePlace: formData.divorcePlace as LocationFormData,
      };
      
      await onRelationshipCreated(relationshipFormData);
      handleReset();
    } catch (error) {
      console.error('Failed to create relationship:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const renderLocationFields = (prefix: 'marriagePlace' | 'divorcePlace', label: string) => (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <PlaceIcon fontSize="small" />
        {label}
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={4}>
          <TextField
            fullWidth
            label="City"
            value={formData[prefix].city}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              [prefix]: { ...prev[prefix], city: e.target.value }
            }))}
            size="small"
          />
        </Grid>
        <Grid item xs={4}>
          <TextField
            fullWidth
            label="State"
            value={formData[prefix].state}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              [prefix]: { ...prev[prefix], state: e.target.value }
            }))}
            size="small"
          />
        </Grid>
        <Grid item xs={4}>
          <TextField
            fullWidth
            label="Country"
            value={formData[prefix].country}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              [prefix]: { ...prev[prefix], country: e.target.value }
            }))}
            size="small"
          />
        </Grid>
      </Grid>
    </Box>
  );

  // Step 1: Person Selection
  const renderPersonSelection = () => (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <SearchIcon />
        Search for Person
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Start typing to search for the person you want to add a relationship with.
      </Typography>

      <Autocomplete
        fullWidth
        options={searchResults}
        getOptionLabel={(option) => `${option.firstName} ${option.lastName}${option.maidenName ? ` (${option.maidenName})` : ''}`}
        renderOption={(props, option) => (
          <Box component="li" {...props}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
              <PersonIcon color="action" />
              <Box sx={{ flex: 1 }}>
                <Typography variant="body1">
                  {option.firstName} {option.lastName}
                  {option.maidenName && (
                    <Typography component="span" variant="body2" color="text.secondary">
                      {` (${option.maidenName})`}
                    </Typography>
                  )}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {option.birthDate && `Born: ${new Date(option.birthDate).getFullYear()}`}
                  {option.birthPlace && ` in ${option.birthPlace}`}
                </Typography>
              </Box>
              <Badge
                badgeContent={option.relevanceScore}
                color="primary"
                sx={{ '& .MuiBadge-badge': { fontSize: '0.7rem' } }}
              />
            </Box>
          </Box>
        )}
        value={selectedPerson}
        onChange={(_, newValue) => handlePersonSelect(newValue)}
        onInputChange={(_, newInputValue) => setSearchTerm(newInputValue)}
        loading={isSearching}
        loadingText="Searching..."
        noOptionsText={searchTerm.length < 2 ? "Type at least 2 characters to search" : "No persons found"}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Search for person"
            variant="outlined"
            InputProps={{
              ...params.InputProps,
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              endAdornment: (
                <>
                  {isSearching ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            helperText="Search by first name, last name, maiden name, or nickname"
          />
        )}
      />

      {selectedPerson && (
        <Paper sx={{ mt: 3, p: 2, backgroundColor: 'primary.50' }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Selected Person
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <PersonIcon color="primary" />
            <Box>
              <Typography variant="body1">
                {selectedPerson.firstName} {selectedPerson.lastName}
                {selectedPerson.maidenName && ` (${selectedPerson.maidenName})`}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedPerson.birthDate && `Born: ${new Date(selectedPerson.birthDate).getFullYear()}`}
                {selectedPerson.birthPlace && ` in ${selectedPerson.birthPlace}`}
              </Typography>
            </Box>
          </Box>
        </Paper>
      )}
    </Box>
  );

  // Step 2: Relationship Type Selection
  const renderRelationshipTypeSelection = () => (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AccountTreeIcon />
        Choose Relationship Type
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Select the type of relationship between you and {selectedPerson?.firstName}.
      </Typography>

      {/* Relationship Type Cards by Category */}
      {['immediate', 'extended', 'other'].map(category => (
        <Box key={category} sx={{ mb: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ textTransform: 'capitalize' }}>
            {category === 'immediate' ? 'Immediate Family' : category === 'extended' ? 'Extended Family' : 'Other Relations'}
          </Typography>
          <Grid container spacing={2}>
            {relationshipTypeOptions
              .filter(option => option.category === category)
              .map(option => (
                <Grid item xs={12} sm={6} md={4} key={option.value}>
                  <Card
                    sx={{
                      cursor: 'pointer',
                      transition: tokens.transitions.normal,
                      border: formData.type === option.value ? `2px solid ${colors.primary.main}` : '1px solid',
                      borderColor: formData.type === option.value ? colors.primary.main : 'divider',
                      backgroundColor: formData.type === option.value ? 'primary.50' : 'background.paper',
                      '&:hover': {
                        boxShadow: tokens.shadows.md,
                        transform: 'translateY(-2px)',
                      },
                    }}
                    onClick={() => handleRelationshipTypeSelect(option.value)}
                  >
                    <CardContent sx={{ textAlign: 'center', py: 2 }}>
                      <Box sx={{ color: formData.type === option.value ? 'primary.main' : 'text.secondary', mb: 1 }}>
                        {option.icon}
                      </Box>
                      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                        {option.label}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {option.description}
                      </Typography>
                      {formData.type === option.value && (
                        <CheckIcon sx={{ color: 'primary.main', mt: 1 }} />
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
          </Grid>
        </Box>
      ))}
    </Box>
  );

  // Step 3: Additional Details
  const renderAdditionalDetails = () => (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <InfoIcon />
        Additional Details
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Add specific information about this {relationshipTypeOptions.find(opt => opt.value === formData.type)?.label.toLowerCase()} relationship.
      </Typography>

      {/* Marriage Information for Spouse */}
      {formData.type === 'spouse' && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FavoriteIcon />
            Marriage Information
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Marriage Date"
                type="date"
                value={formData.marriageDate}
                onChange={(e) => setFormData(prev => ({ ...prev, marriageDate: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: <EventIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Divorce Date"
                type="date"
                value={formData.divorceDate}
                onChange={(e) => setFormData(prev => ({ ...prev, divorceDate: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: <EventIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />
            </Grid>
            <Grid item xs={12}>
              {renderLocationFields('marriagePlace', 'Marriage Location')}
            </Grid>
            {formData.divorceDate && (
              <Grid item xs={12}>
                {renderLocationFields('divorcePlace', 'Divorce Location')}
              </Grid>
            )}
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Marriage End Reason</InputLabel>
                <Select
                  value={formData.marriageEndReason || ''}
                  label="Marriage End Reason"
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    marriageEndReason: e.target.value as 'divorce' | 'death' | 'annulment' | undefined 
                  }))}
                >
                  <MenuItem value="">None</MenuItem>
                  <MenuItem value="divorce">Divorce</MenuItem>
                  <MenuItem value="death">Death</MenuItem>
                  <MenuItem value="annulment">Annulment</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Parent-Child Relationship Details */}
      {(formData.type === 'parent' || formData.type === 'child' || 
        formData.type === 'adoptive-parent' || formData.type === 'adoptive-child') && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FamilyIcon />
            Relationship Details
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isAdopted}
                    onChange={(e) => setFormData(prev => ({ ...prev, isAdopted: e.target.checked }))}
                  />
                }
                label="Adopted"
              />
            </Grid>
            <Grid item xs={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isBiological}
                    onChange={(e) => setFormData(prev => ({ ...prev, isBiological: e.target.checked }))}
                  />
                }
                label="Biological"
              />
            </Grid>
            <Grid item xs={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isStepRelationship}
                    onChange={(e) => setFormData(prev => ({ ...prev, isStepRelationship: e.target.checked }))}
                  />
                }
                label="Step Relationship"
              />
            </Grid>
            {formData.isAdopted && (
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Adoption Date"
                  type="date"
                  value={formData.adoptionDate}
                  onChange={(e) => setFormData(prev => ({ ...prev, adoptionDate: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            )}
          </Grid>
        </Box>
      )}

      {/* Notes and Sources */}
      <Box>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Additional Information
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Notes"
              multiline
              rows={3}
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              helperText="Any additional notes about this relationship"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Sources"
              value={formData.sources}
              onChange={(e) => setFormData(prev => ({ ...prev, sources: e.target.value }))}
              helperText="Comma-separated list of sources documenting this relationship"
              InputProps={{
                startAdornment: <SourceIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );

  // Step 4: Review and Create
  const renderReviewAndCreate = () => (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CheckIcon />
        Review Relationship
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Please review the relationship details before creating.
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Relationship Summary
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">Person</Typography>
            <Typography variant="body1">{selectedPerson?.firstName} {selectedPerson?.lastName}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">Relationship Type</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {relationshipTypeOptions.find(opt => opt.value === formData.type)?.icon}
              <Typography variant="body1">
                {relationshipTypeOptions.find(opt => opt.value === formData.type)?.label}
              </Typography>
            </Box>
          </Grid>
          
          {formData.type === 'spouse' && formData.marriageDate && (
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" fontWeight={600}>Marriage Details</Typography>
              <Typography variant="body2">
                Date: {new Date(formData.marriageDate).toLocaleDateString()}
              </Typography>
              {(formData.marriagePlace.city || formData.marriagePlace.state || formData.marriagePlace.country) && (
                <Typography variant="body2">
                  Location: {[formData.marriagePlace.city, formData.marriagePlace.state, formData.marriagePlace.country]
                    .filter(Boolean).join(', ')}
                </Typography>
              )}
            </Grid>
          )}
          
          {formData.notes && (
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" fontWeight={600}>Notes</Typography>
              <Typography variant="body2">{formData.notes}</Typography>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Validation Results */}
      {validation && (
        <Box sx={{ mb: 3 }}>
          {validation.errors.length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Validation Errors:
              </Typography>
              {validation.errors.map((error: any, index: number) => (
                <Typography key={index} variant="body2">
                  • {error.message}
                </Typography>
              ))}
            </Alert>
          )}
          
          {validation.warnings.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Warnings:
              </Typography>
              {validation.warnings.map((warning: any, index: number) => (
                <Typography key={index} variant="body2">
                  • {warning.message}
                  {warning.suggestion && ` (${warning.suggestion})`}
                </Typography>
              ))}
            </Alert>
          )}
          
          {validation.isValid && validation.errors.length === 0 && (
            <Alert severity="success">
              <Typography variant="body2">
                Relationship validation passed. Ready to create!
              </Typography>
            </Alert>
          )}
        </Box>
      )}
    </Box>
  );

  const canProceedToNext = () => {
    switch (activeStep) {
      case 0: return selectedPerson !== null;
      case 1: return Boolean(formData.type);
      case 2: return true; // Details are optional
      case 3: return validation?.isValid && !isValidating;
      default: return false;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '600px' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AddIcon />
          Build New Relationship
          {isValidating && <CircularProgress size={16} />}
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Stepper activeStep={activeStep} orientation="vertical">
            {stepTitles.map((label, index) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
                <StepContent>
                  <Box sx={{ mb: 2 }}>
                    {index === 0 && renderPersonSelection()}
                    {index === 1 && renderRelationshipTypeSelection()}
                    {index === 2 && renderAdditionalDetails()}
                    {index === 3 && renderReviewAndCreate()}
                  </Box>
                  
                  <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                    {index === stepTitles.length - 1 ? (
                      <Button
                        variant="contained"
                        onClick={handleCreate}
                        disabled={!canProceedToNext() || isLoading}
                        startIcon={isLoading ? <CircularProgress size={16} /> : <CheckIcon />}
                      >
                        {isLoading ? 'Creating...' : 'Create Relationship'}
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        onClick={handleNext}
                        disabled={!canProceedToNext()}
                        endIcon={<NavigateNextIcon />}
                      >
                        Next
                      </Button>
                    )}
                    
                    {index > 0 && (
                      <Button
                        onClick={handleBack}
                        startIcon={<NavigateBeforeIcon />}
                      >
                        Back
                      </Button>
                    )}
                  </Box>
                </StepContent>
              </Step>
            ))}
          </Stepper>
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onCancel} disabled={isLoading}>
          Cancel
        </Button>
        <Button onClick={handleReset} disabled={isLoading}>
          Reset
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RelationshipBuilder;